import { useEffect } from "react";
import StPetersburgHeroSection from "./components/HeroSection";
import StPetersburgStickyNav from "./components/StickyNav";
import StPetersburgProcessingTimeSection from "./components/ProcessingTimeSection";
import StPetersburgServicesSection from "./components/ServicesSection";
import StPetersburgLocalAreaSection from "./components/LocalAreaSection";
import StPetersburgTravelTimingSection from "./components/TravelTimingSection";
import StPetersburgDestinationsSection from "./components/DestinationsSection";
import StPetersburgTestimonialsSection from "./components/TestimonialsSection";
import StPetersburgAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import StPetersburgComparisonSection from "./components/ComparisonSection";
import StPetersburgMistakesSection from "./components/MistakesSection";
import StPetersburgHowItWorksSection from "./components/HowItWorksSection";
import StPetersburgFAQSection, { faqs } from "./components/FAQSection";
import StPetersburgHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import StPetersburgFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function StPetersburgPage() {
  useEffect(() => {
    document.title = "Emergency Passport St. Petersburg, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/st-petersburg/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in St. Petersburg, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Pinellas County travelers, Tampa Bay cruise passengers, and art district visitors.");
    setMeta("geo.placename", "St. Petersburg, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "27.7676;-82.6403");
    setMeta("ICBM", "27.7676, -82.6403");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "St. Petersburg Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in St. Petersburg — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for St. Petersburg and Pinellas County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "St. Petersburg",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "St. Petersburg",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div>
      <StPetersburgHeroSection />
      <StPetersburgStickyNav />
      <StPetersburgProcessingTimeSection />
      <StPetersburgServicesSection />
      <StPetersburgLocalAreaSection />
      <StPetersburgTravelTimingSection />
      <StPetersburgDestinationsSection />
      <StPetersburgTestimonialsSection />
      <StPetersburgAcceptanceFacilitiesSection />
      <StPetersburgComparisonSection />
      <StPetersburgMistakesSection />
      <StPetersburgHowItWorksSection />
      <StPetersburgFAQSection />
      <StPetersburgHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/st-petersburg" state="FL" />
      <StPetersburgFinalCTA />
    </div>
  );
}